import React, { useState, useRef, useEffect, useMemo } from 'react';

import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Modal,
  Pressable,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import ReactDOM from 'react-dom';
import { FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb } from '../../components';
import { computePortalPosition } from '../../utils/portalPosition';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { kpisApi, talentKPILinksApi, talentProjectsApi, talentActivitiesApi, talentSpecializationsApi } from '../../services/apiService';
import api from '../../services/api';
import { KPIType, Frequency, Polarity, TargetAggregation } from '../../types';
import { computeGrid, overallTotal } from '../../utils/targetGrid';
import KpiAnnualTargetSection, {
  KpiTargetConfig,
  emptyKpiTargetConfig,
  buildKpiTargetPayload,
  validateKpiTargetConfig,
} from './components/KpiAnnualTargetSection';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive } from '../../hooks';
import { normalizeArabicNumbers } from '../../utils/rtl';

type RouteProps = RouteProp<MainStackParamList, 'AddTalentKPI'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
}

const Dropdown = ({ value, options, placeholder, onChange }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBg: colors.card,
    itemActiveBg: isDark ? '#451A1A' : '#FEF2F2',
  };

  const handleOpen = () => {
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    // Close only for clicks that land outside the menu. The menu is portalled
    // to document.body, so a plain "any mouseup closes it" listener also fires
    // for clicks on the options themselves — unmounting the menu before the
    // option's press resolves, which silently dropped the selection.
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[data-dropdown-menu="kpi"]')) return;
      setIsOpen(false);
    };
    // Follow the trigger on scroll/resize instead of closing (so the last
    // dropdown on a page can be scrolled into view).
    const reposition = () => {
      buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
        setButtonLayout({ x, y, width, height });
      });
    };
    document.addEventListener('mouseup', handleClickOutside);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  const DROPDOWN_MAX_H = 200;
  const dropPos = computePortalPosition(buttonLayout, buttonLayout.width || 200, DROPDOWN_MAX_H);
  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      style={{
        position: 'fixed' as any,
        top: dropPos.top,
        left: dropPos.left,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: 1,
        borderColor: dropdownTheme.menuBorder,
        borderRadius: 12,
        paddingVertical: 8,
        zIndex: 99999,
        elevation: 99999,
        maxHeight: 200,
      }}
      // Marker read by the outside-click listener so clicks on options don't
      // close the menu before their press resolves.
      // @ts-ignore - RN Web renders dataSet as data-* attributes
      dataSet={{ dropdownMenu: 'kpi' }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
      // @ts-ignore - web style
      onMouseUp={(e: any) => e.stopPropagation()}
    >
      <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
        <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.dropdownItem,
                { backgroundColor: dropdownTheme.itemBg },
                value === option.value && { backgroundColor: dropdownTheme.itemActiveBg }
              ]}
              onPress={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <Text style={[
                styles.dropdownItemText,
                { color: dropdownTheme.text },
                value === option.value && styles.dropdownItemTextActive
              ]}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    </View>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
        ]}
        onPress={handleOpen}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[
          styles.dropdownButtonText,
          { color: dropdownTheme.text },
          !selectedOption && { color: dropdownTheme.placeholder }
        ]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// Content Type Resolver
// ============================================================

/**
 * Resolves the Django ContentType PK for a given model name.
 * Uses the /resolve-content-type/ endpoint. Falls back to extracting
 * the content_type from existing talent-kpi-links if available.
 */
async function resolveContentTypeId(modelName: string, objectId?: number): Promise<number> {
  // Strategy 1: Try the dedicated endpoint
  try {
    const response = await api.get('/resolve-content-type/', {
      params: { app_label: 'talents', model: modelName },
    });
    if (response.data?.id) return response.data.id;
  } catch {
    // Endpoint may not exist yet, fall through to strategy 2
  }

  // Strategy 2: Fetch existing links for this entity and extract content_type
  if (objectId) {
    try {
      const response = await api.get('/talent-kpi-links/', {
        params: { object_id: objectId, page_size: 1 },
      });
      const results = response.data?.results || response.data;
      if (Array.isArray(results) && results.length > 0 && results[0].content_type) {
        return results[0].content_type;
      }
    } catch {
      // Fall through
    }
  }

  throw new Error(`Could not resolve ContentType ID for model "${modelName}"`);
}

// ============================================================
// Helper: map route params to entity info
// ============================================================

type EntityType = 'specialization' | 'project' | 'activity' | 'student';

interface EntityInfo {
  entityType: EntityType;
  modelName: string;
  entityId: number;
  sourceType: string;
}

function getEntityInfo(params: {
  specializationId?: number;
  projectId?: number;
  activityId?: number;
  studentId?: number;
}): EntityInfo | null {
  if (params.activityId) {
    return { entityType: 'activity', modelName: 'talentactivity', entityId: params.activityId, sourceType: 'activity' };
  }
  if (params.projectId) {
    return { entityType: 'project', modelName: 'talentproject', entityId: params.projectId, sourceType: 'project' };
  }
  if (params.studentId) {
    return { entityType: 'student', modelName: 'talentstudent', entityId: params.studentId, sourceType: 'student' };
  }
  if (params.specializationId) {
    return { entityType: 'specialization', modelName: 'talentspecialization', entityId: params.specializationId, sourceType: 'specialization' };
  }
  return null;
}

// ============================================================
// Custom Date Picker (same as AddTalentActivityScreen)
// ============================================================
const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const AR_DAYS_SHORT = ['أح', 'إث', 'ثل', 'أر', 'خم', 'جم', 'سب'];
const EN_DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function buildMonthCells(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells: { day: number; dateStr: string; isCurrentMonth: boolean }[] = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  return cells;
}

interface SingleDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  placeholder?: string;
}

// Legacy in-file picker — superseded by the shared components/SingleDatePicker. Unused.
const _LegacySingleDatePicker = ({ value, onChange, minDate, placeholder }: SingleDatePickerProps) => {
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<'days' | 'months' | 'years'>('days');
  const [layout, setLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const btnRef = useRef<View>(null);

  const initMonth = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(initMonth.getFullYear());
  const [viewMonth, setViewMonth] = useState(initMonth.getMonth());
  const minYear = minDate ? new Date(minDate).getFullYear() : new Date().getFullYear();
  const minMonthIndex = minDate ? new Date(minDate).getMonth() : new Date().getMonth();
  const [yearRangeStart, setYearRangeStart] = useState(minYear);

  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const handleOpen = () => {
    if (isOpen) { setIsOpen(false); return; }
    const d = value ? new Date(value) : new Date();
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setYearRangeStart(minYear);
    setPickerMode('days');
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setLayout({ x, y, width: w, height: h });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const handleSelect = (dateStr: string) => { onChange(dateStr); setIsOpen(false); };

  const months = isRTL ? AR_MONTHS : EN_MONTHS;
  const dayHeaders = isRTL ? AR_DAYS_SHORT : EN_DAYS_SHORT;
  const cells = buildMonthCells(viewYear, viewMonth);
  const currentYear = new Date().getFullYear();
  const yearRange = Array.from({ length: 12 }, (_, i) => yearRangeStart + i);

  const displayValue = value
    ? (() => { const [y, m, d] = value.split('-'); return `${d}/${m}/${y}`; })()
    : '';

  const renderHeader = () => {
    if (pickerMode === 'years') {
      return (
        <View style={dpStyles.monthNav}>
          <TouchableOpacity onPress={() => setYearRangeStart(yearRangeStart + 12)} style={dpStyles.navBtn}>
            <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
          </TouchableOpacity>
          <Text style={[dpStyles.monthLabel, { color: colors.textPrimary }]}>{yearRangeStart} - {yearRangeStart + 11}</Text>
          <TouchableOpacity onPress={() => { if (yearRangeStart - 12 >= minYear) setYearRangeStart(yearRangeStart - 12); }} style={dpStyles.navBtn} disabled={yearRangeStart <= minYear}>
            <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
          </TouchableOpacity>
        </View>
      );
    }
    if (pickerMode === 'months') {
      return (
        <View style={dpStyles.monthNav}>
          <TouchableOpacity onPress={() => { setViewYear(viewYear + 1); }} style={dpStyles.navBtn}>
            <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setPickerMode('years')}>
            <Text style={[dpStyles.monthLabel, { color: '#a9211f' }]}>{viewYear}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => { if (viewYear > minYear) setViewYear(viewYear - 1); }} style={dpStyles.navBtn} disabled={viewYear <= minYear}>
            <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={viewYear <= minYear ? '#CBD5E1' : colors.textPrimary} /></View>
          </TouchableOpacity>
        </View>
      );
    }
    return (
      <View style={dpStyles.monthNav}>
        <TouchableOpacity onPress={nextMonth} style={dpStyles.navBtn}>
          <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setPickerMode('months')}>
          <Text style={[dpStyles.monthLabel, { color: '#a9211f' }]}>{months[viewMonth]} {viewYear}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={prevMonth} style={dpStyles.navBtn}>
          <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
        </TouchableOpacity>
      </View>
    );
  };

  const renderBody = () => {
    if (pickerMode === 'years') {
      return (
        <View style={dpStyles.pickerGrid}>
          {yearRange.map(yr => {
            const isDisabled = yr < minYear;
            return (
              <TouchableOpacity key={yr} disabled={isDisabled} style={[dpStyles.pickerCell, yr === viewYear && { backgroundColor: '#a9211f', borderRadius: 8 }, yr === currentYear && yr !== viewYear && { backgroundColor: isDark ? '#333' : '#F1F5F9', borderRadius: 8 }]} onPress={() => { setViewYear(yr); setPickerMode('months'); }}>
                <Text style={[dpStyles.pickerCellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, yr === viewYear && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{yr}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      );
    }
    if (pickerMode === 'months') {
      return (
        <View style={dpStyles.pickerGrid}>
          {months.map((m, idx) => {
            const isDisabled = viewYear === minYear && idx < minMonthIndex;
            return (
              <TouchableOpacity key={idx} disabled={isDisabled} style={[dpStyles.pickerCell, idx === viewMonth && { backgroundColor: '#a9211f', borderRadius: 8 }]} onPress={() => { setViewMonth(idx); setPickerMode('days'); }}>
                <Text style={[dpStyles.pickerCellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, idx === viewMonth && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{m}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      );
    }
    return (
      <>
        <View style={dpStyles.dayHeaderRow}>
          {dayHeaders.map(h => <Text key={h} style={[dpStyles.dayHeaderText, { color: colors.textMuted }]}>{h}</Text>)}
        </View>
        <View style={dpStyles.grid}>
          {cells.map((cell, idx) => {
            const isSelected = cell.dateStr === value;
            const isToday = cell.dateStr === todayStr;
            const isDisabled = !cell.isCurrentMonth || (minDate ? cell.dateStr < minDate : false);
            return (
              <TouchableOpacity key={idx} style={[dpStyles.cell, isSelected && { backgroundColor: '#a9211f', borderRadius: 20 }, isToday && !isSelected && { backgroundColor: isDark ? '#333' : '#F1F5F9', borderRadius: 20 }]} onPress={() => !isDisabled && handleSelect(cell.dateStr)} disabled={isDisabled}>
                <Text style={[dpStyles.cellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, isSelected && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{cell.day}</Text>
              </TouchableOpacity>
              );
            })}
          </View>
        </>
      );
    };

  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 } as any} onClick={() => setIsOpen(false)} />
      <div style={{ position: 'fixed', top: layout.y + layout.height + 4, left: Math.max(8, layout.x + layout.width - 320), zIndex: 99999 } as any}>
        <View style={[dpStyles.panel, { backgroundColor: colors.card, borderColor: colors.border }]}>
          {renderHeader()}
          {renderBody()}
        </View>
      </div>
    </>,
    document.body,
  ) : null;

  return (
    <View ref={btnRef}>
      <TouchableOpacity onPress={handleOpen} style={[dpStyles.button, { backgroundColor: isDark ? colors.backgroundAlt : '#F9FAFB', borderColor: colors.border }]}>
        <CalendarIcon size={16} color={colors.textMuted} />
        <Text style={[dpStyles.buttonText, { color: value ? colors.textPrimary : colors.textMuted }]}>
          {displayValue || placeholder || (isRTL ? 'اختر التاريخ' : 'Select date')}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

const dpStyles = StyleSheet.create({
  button: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  buttonText: { fontSize: 14, fontFamily: FONTS.regular, flex: 1, textAlign: 'right' },
  panel: { width: 320, borderRadius: 12, borderWidth: 1, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 10 },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  navBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  monthLabel: { fontSize: 16, fontFamily: FONTS.medium },
  dayHeaderRow: { flexDirection: 'row', marginBottom: 8 },
  dayHeaderText: { flex: 1, textAlign: 'center', fontSize: 12, fontFamily: FONTS.medium },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: '14.28%' as any, alignItems: 'center', justifyContent: 'center', paddingVertical: 8 },
  cellText: { fontSize: 14, fontFamily: FONTS.regular },
  pickerGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  pickerCell: { width: '30%' as any, alignItems: 'center', justifyContent: 'center', paddingVertical: 12, marginHorizontal: '1.5%' as any },
  pickerCellText: { fontSize: 14, fontFamily: FONTS.regular },
});

// ============================================================
// Main Component
// ============================================================

export default function AddTalentKPIScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { specializationId, projectId, activityId, studentId } = route.params || {};
  const { showSuccess, showError } = useNotification();
  const { isMobile } = useResponsive();
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const entityInfo = getEntityInfo({ specializationId, projectId, activityId, studentId });

  // A general ("مؤشر عام") KPI fans its readings out to the students enrolled
  // in the entity, so it applies to talents, projects and activities — but not
  // to a KPI created for one specific student. Mirrors GENERAL_KPI_ENTITY_MODELS
  // on the backend.
  const supportsGeneralKPI = !!entityInfo
    && ['specialization', 'project', 'activity'].includes(entityInfo.entityType);
  const generalScopeLabel = entityInfo?.entityType === 'project'
    ? (isRTL ? 'المشروع' : 'project')
    : entityInfo?.entityType === 'activity'
      ? (isRTL ? 'النشاط' : 'activity')
      : (isRTL ? 'الموهبة' : 'talent');

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F1F5F9',
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
    checkboxBorder: isDark ? '#4B5563' : '#D1D5DB',
    checkboxBg: colors.card,
    dateInput: colors.card,
    dateInputBorder: colors.border,
  };

  // ============================================================
  // Form state
  // ============================================================
  const [formData, setFormData] = useState({
    // Section 1: Basic Information
    name: '',
    weight: '0',
    description: '',
    responsible: '',
    isPublicKPI: false,
    // Section 2: Classification
    kpiType: '' as KPIType | '',
    polarity: '' as Polarity | '',
    // Section 3: Measurement — the fields KpiAnnualTargetSection reads but the
    // parent owns. Everything else about the target (end date, unit
    // description, baseline, pattern, aggregation, step, grid) lives in
    // targetConfig below, because that is how the cloned SMS section is driven.
    startDate: '',
    unit: '',
    frequency: '' as Frequency | '',
    dataSource: '',
    formula: '',
  });

  // Sections 3 + 4 are the SMS annual-target section, cloned verbatim; its whole
  // state is this one config object.
  const [targetConfig, setTargetConfig] = useState<KpiTargetConfig>(emptyKpiTargetConfig);
  const patchTargetConfig = (patch: Partial<KpiTargetConfig>) =>
    setTargetConfig(prev => ({ ...prev, ...patch }));

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [usedWeight, setUsedWeight] = useState(0);

  // Fetch existing weights for this entity
  // For specializations: sum of KPI weights + project weights + activity weights = 100%
  // For other entities: sum of KPI weights only = 100%
  interface WeightItem { id: number; name: string; type: 'kpi' | 'project' | 'activity'; weight: number; linkId?: number }
  const [weightBreakdown, setWeightBreakdown] = useState({ kpis: 0, projects: 0, activities: 0 });
  const [weightItems, setWeightItems] = useState<WeightItem[]>([]);
  const [specName, setSpecName] = useState('');
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [modalWeights, setModalWeights] = useState<Record<string, string>>({});
  const [savingWeights, setSavingWeights] = useState(false);

  const fetchWeights = async () => {
    if (!entityInfo) return;
    try {
      const ctResp = await api.get('/resolve-content-type/', { params: { model: entityInfo.modelName } });
      const ctId = ctResp.data?.id;
      const params: any = { object_id: entityInfo.entityId };
      if (ctId) params.content_type = ctId;
      const links = await talentKPILinksApi.list(params);
      const items = links?.results || links || [];
      const kpiTotal = items.reduce((sum: number, link: any) => sum + (parseFloat(link.kpi_weight) || 0), 0);

      const allItems: WeightItem[] = items.map((link: any) => ({
        id: link.kpi || link.id,
        name: `${link.kpi_name || ''}${isRTL ? ' (مؤشر أداء)' : ' (KPI)'}`,
        type: 'kpi' as const,
        weight: parseFloat(link.kpi_weight) || 0,
        linkId: link.id,
      }));

      let projTotal = 0;
      let actTotal = 0;

      if (entityInfo.entityType === 'specialization') {
        try {
          const projResp = await talentProjectsApi.list({ specialization: entityInfo.entityId, page_size: 200 });
          const projects = projResp?.results || projResp || [];
          for (const proj of projects) {
            const pw = parseFloat(proj.weight) || 0;
            projTotal += pw;
            allItems.push({ id: proj.id, name: `${proj.name}${isRTL ? ' (مشروع)' : ' (Project)'}`, type: 'project', weight: pw });
            try {
              const actResp = await talentActivitiesApi.list({ project: proj.id, page_size: 200 });
              const activities = actResp?.results || actResp || [];
              for (const a of activities) {
                const aw = parseFloat(a.weight) || 0;
                actTotal += aw;
                allItems.push({ id: a.id, name: `${a.name}${isRTL ? ' (نشاط)' : ' (Activity)'}`, type: 'activity', weight: aw });
              }
            } catch { /* silent */ }
          }
          // Fetch specialization name
          try {
            const specResp = await talentSpecializationsApi.get(entityInfo.entityId);
            if (specResp?.name) setSpecName(specResp.name);
          } catch { /* silent */ }
        } catch { /* silent */ }
      }

      setWeightItems(allItems);
      setWeightBreakdown({ kpis: kpiTotal, projects: projTotal, activities: actTotal });
      setUsedWeight(kpiTotal + projTotal + actTotal);
    } catch {
      // silent — assume 0
    }
  };

  useEffect(() => { fetchWeights(); }, [entityInfo?.entityId, entityInfo?.modelName]);
  const isSpecialization = entityInfo?.entityType === 'specialization';

  // Weight distribution modal helpers
  const currentWeight = parseFloat(formData.weight) || 0;
  const openWeightModal = () => {
    const wts: Record<string, string> = {};
    weightItems.forEach((item, i) => { wts[`${item.type}-${item.id}-${i}`] = String(item.weight); });
    setModalWeights(wts);
    setShowWeightModal(true);
  };
  const modalTotal = Object.values(modalWeights).reduce((sum, v) => { const n = parseFloat(v); return sum + (isNaN(n) ? 0 : n); }, 0) + (isNaN(currentWeight) ? 0 : currentWeight);
  const modalRemaining = Math.round((100 - modalTotal) * 100) / 100;
  const autoRedistribute = () => {
    const count = Object.keys(modalWeights).length + 1; // +1 for current KPI
    const perItem = Math.floor((100 / count) * 100) / 100;
    const wts: Record<string, string> = {};
    Object.keys(modalWeights).forEach(k => { wts[k] = String(perItem); });
    setModalWeights(wts);
    updateField('weight', String(perItem));
  };
  const handleSaveWeights = async () => {
    if (modalRemaining < 0) return;
    setSavingWeights(true);
    try {
      for (const item of weightItems) {
        const key = Object.keys(modalWeights).find(k => k.startsWith(`${item.type}-${item.id}-`));
        const newWeight = parseFloat(key ? modalWeights[key] : '0') || 0;
        if (Math.abs(newWeight - item.weight) < 0.01) continue; // skip unchanged
        if (item.type === 'kpi' && item.linkId) {
          // The weight lives on the KPI and is reached through its link. The
          // old /kpis/<id>/ path is an SMS route that does not exist here, so
          // every save 404'd and the silent catch hid it.
          await api.patch(`/talent-kpi-links/${item.linkId}/set-weight/`, { weight: newWeight });
        } else if (item.type === 'project') {
          await talentProjectsApi.update(item.id, { weight: newWeight });
        } else if (item.type === 'activity') {
          await talentActivitiesApi.update(item.id, { weight: newWeight });
        }
      }
      setShowWeightModal(false);
      await fetchWeights(); // refresh
    } catch (e: any) {
      // Never silent: a failed save used to close the modal as if it worked.
      showError(
        e?.response?.data?.error
        || e?.response?.data?.detail
        || (isRTL ? 'تعذر حفظ الأوزان' : 'Could not save weights'),
      );
    } finally {
      setSavingWeights(false);
    }
  };

  const remainingWeight = Math.round((100 - usedWeight - currentWeight) * 100) / 100;
  const isWeightExceeded = remainingWeight < 0;

  // ============================================================
  // Dropdown options
  // ============================================================
  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: isRTL ? 'استراتيجي' : 'Strategic' },
    { value: 'operational', label: isRTL ? 'تشغيلي' : 'Operational' },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: isRTL ? 'تصاعدي' : 'Ascending' },
    { value: 'negative', label: isRTL ? 'تنازلي' : 'Descending' },
  ];

  // ============================================================
  // Annual target grid — computed by the same engine the section renders
  // (utils/targetGrid.ts), so this total is exactly what the user sees in the
  // "المستهدف الكلي" cell.
  // ============================================================
  const gridCells = React.useMemo(() => {
    if (!formData.startDate || !targetConfig.verificationEndDate || !formData.frequency) return [];
    try {
      return computeGrid({
        start: formData.startDate,
        end: targetConfig.verificationEndDate,
        frequency: formData.frequency as Frequency,
        pattern: targetConfig.targetPattern,
        unit: formData.unit,
        stepPercent: parseFloat(targetConfig.targetStepPercent) || 0,
        stepAmount: parseFloat(targetConfig.targetStepAmount) || 0,
        inputs: targetConfig.inputs,
      });
    } catch {
      return [];
    }
  }, [formData.startDate, formData.frequency, formData.unit, targetConfig]);

  // A percentage KPI cannot be summed across periods — the backend's
  // kpi_targets.overall_total() returns the last cell for a percent unit
  // whatever aggregation was stored, so the section forces 'last' too.
  const computedTotalTarget = overallTotal(
    gridCells.map(c => c.value),
    formData.unit,
    (formData.unit === '%' ? 'last' : targetConfig.targetAggregation || 'sum') as TargetAggregation,
  );

  // ============================================================
  // Validation
  // ============================================================
  // The cloned section renders its own errors through t('validation.<key>'),
  // so its slice of the error map holds KEYS. Sections 1-2 keep the plain
  // strings they always used.
  const [targetErrors, setTargetErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const newTargetErrors: Record<string, string> = validateKpiTargetConfig(
      targetConfig, { startDate: formData.startDate },
    );

    if (!formData.name.trim()) {
      newErrors.name = isRTL ? 'اسم المؤشر مطلوب' : 'KPI name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = isRTL ? 'وصف المؤشر مطلوب' : 'KPI description is required';
    }
    if (!formData.kpiType) {
      newErrors.kpiType = isRTL ? 'تصنيف المؤشر مطلوب' : 'KPI classification is required';
    }
    if (!formData.polarity) {
      newErrors.polarity = isRTL ? 'اتجاه المؤشر مطلوب' : 'Polarity is required';
    }
    if (!formData.unit) {
      newTargetErrors.unit = 'unitRequired';
    }
    if (!formData.frequency) {
      newTargetErrors.frequency = 'frequencyRequired';
    }
    if (!formData.startDate) {
      newTargetErrors.startDate = 'startDateRequired';
    }
    if (!targetConfig.verificationEndDate) {
      newTargetErrors.verificationEndDate = 'endDateRequired';
    }

    setErrors(newErrors);
    setTargetErrors(newTargetErrors);
    return Object.keys(newErrors).length === 0 && Object.keys(newTargetErrors).length === 0;
  };

  // ============================================================
  // Save handler
  // ============================================================
  const handleSave = async () => {
    if (!validateForm()) {
      showError(isRTL ? 'يرجى تعبئة الحقول المطلوبة' : 'Please fill required fields');
      return;
    }

    if (!entityInfo) {
      showError(isRTL ? 'لم يتم تحديد الكيان المرتبط' : 'No linked entity specified');
      return;
    }

    if (isWeightExceeded) {
      showError(isRTL
        ? isSpecialization
          ? 'مجموع أوزان المؤشرات والمشاريع والأنشطة يتجاوز 100% — يرجى تقليل الوزن'
          : 'مجموع الأوزان يتجاوز 100% — يرجى تقليل الوزن'
        : isSpecialization
          ? 'Total weight of KPIs, projects, and activities exceeds 100% — please reduce the weight'
          : 'Total weight exceeds 100% — please reduce the weight');
      return;
    }

    setLoading(true);
    try {
      // The target half of the payload — scalar fields plus the authored
      // target_values[] grid — is built by the same helper SMS uses. Its
      // `baseline_value` is renamed here because the Talents model calls that
      // column `baseline`.
      const { baseline_value: baselineValue, ...targetPayload } = buildKpiTargetPayload(
        targetConfig,
        { unit: formData.unit, frequency: formData.frequency, startDate: formData.startDate },
      );

      // Single-step: create KPI + link via talent-kpi-links/create-with-kpi/
      await api.post('/talent-kpi-links/create-with-kpi/', {
        kpi_data: {
          name: formData.name.trim(),
          description: formData.description.trim(),
          kpi_type: formData.kpiType as KPIType,
          polarity: formData.polarity as Polarity,
          unit: formData.unit,
          frequency: formData.frequency as Frequency,
          weight: parseFloat(formData.weight) || 0,
          formula: formData.formula.trim(),
          data_source: formData.dataSource.trim(),
          ...targetPayload,
          baseline: baselineValue,
          measurement_start_date: formData.startDate || null,
          target_value: computedTotalTarget > 0 ? computedTotalTarget.toFixed(2) : '0.00',
          // Overall/general only applies on a specialization (fans to students).
          is_general: supportsGeneralKPI ? formData.isPublicKPI : false,
        },
        entity_model: entityInfo.modelName,
        entity_id: entityInfo.entityId,
        target_value: computedTotalTarget > 0 ? computedTotalTarget.toFixed(2) : '0.00',
      });

      showSuccess(isRTL ? 'تم إضافة مؤشر الأداء بنجاح' : 'KPI added successfully');
      navigation.goBack();
    } catch (error: any) {
      console.error('Error creating Talent KPI:', error);
      const errorMessage =
        error.response?.data?.detail
        || error.response?.data?.name?.[0]
        || error.message
        || (isRTL ? 'فشل في إضافة مؤشر الأداء' : 'Failed to add KPI');
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <PageContainer>
      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.content, { padding: 24 }]}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: isRTL ? 'الرئيسية' : 'Home' },
            { label: isRTL ? 'مبادرة مواهب' : 'Talents Initiative', route: 'Talents' as any },
            { label: isRTL ? 'إضافة مؤشر أداء' : 'Add KPI' },
          ]}
        />

        {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>
            {isRTL ? 'إضافة مؤشر أداء' : 'Add Performance Indicator'}
          </Text>

          {/* ============================================ */}
          {/* Section 1: Basic Information */}
          {/* ============================================ */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
            </Text>

            {/* Row 1: KPI Name + Weight */}
            <View style={[styles.row, isMobile && { flexDirection: 'column' }]}>
              {/* Weight Field */}
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'الوزن' : 'Weight'}
                  </Text>
                </View>
                <View style={styles.weightInputRow}>
                  <View style={styles.setWeightLabel}>
                    <Text style={styles.setWeightButtonText}>
                      {isRTL ? 'تعيين الوزن' : 'Set Weight'}
                    </Text>
                  </View>
                  <Text style={styles.percentSymbol}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: isWeightExceeded ? '#DC2626' : themeColors.inputBorder, color: themeColors.inputText }]}
                    value={formData.weight}
                    onChangeText={(v) => updateField('weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
                <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: isWeightExceeded ? '#DC2626' : '#64748B', textAlign: isRTL ? 'right' : 'left', marginTop: 4 }}>
                  {isRTL
                    ? `المتبقي: %${Math.max(0, Math.round(100 - usedWeight - currentWeight))} — المستخدم: %${Math.round(usedWeight + currentWeight)}`
                    : `Remaining: ${Math.max(0, Math.round(100 - usedWeight - currentWeight))}% — Used: ${Math.round(usedWeight + currentWeight)}%`}
                </Text>
                {isSpecialization && (weightBreakdown.projects > 0 || weightBreakdown.activities > 0) && (
                  <Text style={{ fontSize: 11, fontFamily: FONTS.regular, color: '#94A3B8', textAlign: isRTL ? 'right' : 'left', marginTop: 2 }}>
                    {isRTL
                      ? `(مؤشرات: %${Math.round(weightBreakdown.kpis)}، مشاريع: %${Math.round(weightBreakdown.projects)}، أنشطة: %${Math.round(weightBreakdown.activities)})`
                      : `(KPIs: ${Math.round(weightBreakdown.kpis)}%, Projects: ${Math.round(weightBreakdown.projects)}%, Activities: ${Math.round(weightBreakdown.activities)}%)`}
                  </Text>
                )}
                {isWeightExceeded && (
                  <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: '#DC2626', textAlign: isRTL ? 'right' : 'left', marginTop: 2 }}>
                    {isRTL
                      ? isSpecialization
                        ? '⚠ مجموع أوزان المؤشرات والمشاريع والأنشطة يتجاوز 100%'
                        : '⚠ مجموع الأوزان يتجاوز 100%'
                      : isSpecialization
                        ? '⚠ Total weight of KPIs, projects, and activities exceeds 100%'
                        : '⚠ Total weight exceeds 100%'}
                  </Text>
                )}
                {isSpecialization && weightItems.length > 0 && (
                  <TouchableOpacity
                    onPress={openWeightModal}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6, alignSelf: isRTL ? 'flex-end' : 'flex-start' }}
                  >
                    <Svg width={14} height={14} viewBox="0 0 24 24" fill="none">
                      <Path d="M12 3v18M3 12h18" stroke="#A9211F" strokeWidth={2} strokeLinecap="round" />
                    </Svg>
                    <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: '#A9211F' }}>
                      {isRTL ? 'توزيع الأوزان' : 'Distribute Weights'}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>

              {/* KPI Name Field */}
              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'اسم المؤشر' : 'KPI Name'}
                  </Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name ? styles.inputError : undefined]}
                  value={formData.name}
                  onChangeText={(v) => updateField('name', v)}
                  placeholder={isRTL ? 'أدخل اسم المؤشر' : 'Enter KPI name'}
                  placeholderTextColor="#9CA3AF"
                  textAlign="right"
                />
                {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
              </View>
            </View>

            {/* Description Field */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {isRTL ? 'وصف المؤشر' : 'KPI Description'}
                </Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.description ? styles.inputError : undefined]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder={isRTL ? 'أدخل وصف المؤشر' : 'Enter KPI description'}
                placeholderTextColor="#9CA3AF"
                textAlign="right"
                multiline
                numberOfLines={3}
              />
              {errors.description && <Text style={styles.errorText}>{errors.description}</Text>}
            </View>

            {/* Responsible Field */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {isRTL ? 'المسؤول' : 'Responsible'}
                </Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.responsible}
                onChangeText={(v) => updateField('responsible', v)}
                placeholder={isRTL ? 'حدد المسؤول عن المؤشر' : 'KPI owner'}
                placeholderTextColor="#9CA3AF"
                textAlign="right"
              />
            </View>

            {/* Overall / general KPI toggle — available wherever the KPI has
                enrolled students to fan out to (talent, project, activity).
                Hidden when a KPI is added straight for one student. */}
            {supportsGeneralKPI && (
              <View style={styles.toggleRow}>
                <View style={styles.toggleTextWrap}>
                  <Text style={[styles.toggleLabel, { color: themeColors.label }]}>
                    {isRTL ? 'مؤشر عام' : 'Overall KPI'}
                  </Text>
                  <Text style={[styles.toggleDescription, { color: themeColors.placeholder }]}>
                    {isRTL
                      ? `مؤشر عام (يتم تطبيق المؤشر على جميع الطلاب المندرجين تحت ${generalScopeLabel}، يحسب الإنجاز بشكل إفرادي لكل طالب ويظهر المتوسط العام للإنجاز)`
                      : `Overall KPI (applied to every student enrolled in the ${generalScopeLabel}. Achievement is calculated per student and the average is displayed.)`}
                  </Text>
                </View>
                <TouchableOpacity
                  activeOpacity={0.8}
                  onPress={() => setFormData(prev => ({ ...prev, isPublicKPI: !prev.isPublicKPI }))}
                  style={[
                    styles.customToggleTrack,
                    { backgroundColor: formData.isPublicKPI ? '#A9211F' : '#D1D5DB' },
                  ]}
                >
                  <View style={[
                    styles.customToggleThumb,
                    formData.isPublicKPI ? styles.customToggleThumbOn : styles.customToggleThumbOff,
                  ]} />
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* ============================================ */}
          {/* Section 2: Classification */}
          {/* ============================================ */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: 30 }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {isRTL ? 'التصنيف' : 'Classification'}
            </Text>

            <View style={[styles.row, { zIndex: 20 }, isMobile && { flexDirection: 'column' }]}>
              {/* Polarity */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'اتجاه المؤشر (النمطية)' : 'Polarity'}
                  </Text>
                </View>
                <Dropdown
                  value={formData.polarity}
                  options={polarityOptions}
                  placeholder={isRTL ? 'اختر اتجاه المؤشر (تصاعدي، تنازلي)' : 'Select polarity (ascending, descending)'}
                  onChange={(v) => updateField('polarity', v)}
                />
                {errors.polarity && <Text style={styles.errorText}>{errors.polarity}</Text>}
              </View>

              {/* KPI Classification */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'تصنيف المؤشر' : 'KPI Classification'}
                  </Text>
                </View>
                <Dropdown
                  value={formData.kpiType}
                  options={kpiTypeOptions}
                  placeholder={isRTL ? 'اختر تصنيف المؤشر' : 'Select KPI classification'}
                  onChange={(v) => updateField('kpiType', v)}
                />
                {errors.kpiType && <Text style={styles.errorText}>{errors.kpiType}</Text>}
              </View>
            </View>
          </View>

          {/* ============================================ */}
          {/* Sections 3 + 4: Measurement + Annual Targets */}
          {/* ============================================ */}
          {/* Cloned from SMS (KpiAnnualTargetSection). It owns the whole
              القياس block and the القيم المستهدفة سنوياً grid, so the two
              hand-rolled sections that used to live here are gone — keeping
              them alongside would render every control twice. The «مؤشر عام»
              toggle is NOT part of it: that is a Talents-only concept and
              stays in Section 1 above.
              No wrapping <View style={styles.section}> here: the section brings
              its own card (same background as themeColors.section), so nesting
              it would double the padding and draw a box inside a box. */}
          <KpiAnnualTargetSection
            config={targetConfig}
            onChange={patchTargetConfig}
            startDate={formData.startDate}
            unit={formData.unit}
            frequency={formData.frequency}
            formula={formData.formula}
            dataSource={formData.dataSource}
            onMeasurementChange={(field, value) => {
              updateField(field, value);
              if (targetErrors[field]) {
                setTargetErrors(prev => ({ ...prev, [field]: '' }));
              }
            }}
            errors={targetErrors}
          />

          {/* ============================================ */}
          {/* Action Buttons */}
          {/* ============================================ */}
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={[styles.saveButton, loading && styles.saveButtonDisabled]}
              onPress={handleSave}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>
                  {isRTL ? 'حفظ' : 'Save'}
                </Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]}
              onPress={handleCancel}
              disabled={loading}
            >
              <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      {/* Weight Distribution Modal */}
      <Modal visible={showWeightModal} transparent animationType="fade" onRequestClose={() => setShowWeightModal(false)}>
        <Pressable style={wdStyles.overlay} onPress={() => setShowWeightModal(false)}>
          <Pressable style={wdStyles.container} onPress={(e) => e.stopPropagation()}>
            {/* Header */}
            <View style={wdStyles.header}>
              <TouchableOpacity onPress={() => setShowWeightModal(false)} style={wdStyles.closeBtn}>
                <Text style={{ fontSize: 20, color: '#64748B' }}>×</Text>
              </TouchableOpacity>
              <Text style={wdStyles.title}>{isRTL ? 'توزيع الأوزان' : 'Weight Distribution'}</Text>
            </View>

            {/* Specialization name */}
            {specName ? <Text style={wdStyles.specName}>{specName}</Text> : null}

            {/* Description */}
            <Text style={wdStyles.description}>
              {isRTL
                ? 'توزيع أوزان العناصر الفرعية على العنصر الأصل المرتبطة به'
                : 'Distribute sub-item weights across the parent element'}
            </Text>

            {/* Info note */}
            <View style={wdStyles.infoRow}>
              <Text style={wdStyles.infoIcon}>ⓘ</Text>
              <Text style={wdStyles.infoText}>
                {isRTL
                  ? 'يجب أن يتم توزيع الوزن الكلي بشكل كامل بحيث يكون الباقي "0" حتى يتم قبول التوزيع'
                  : 'Total weight must be fully distributed (remaining = 0) for the distribution to be accepted'}
              </Text>
            </View>

            {/* Weight rows */}
            <ScrollView style={{ maxHeight: 320 }}>
              {/* Current KPI being added */}
              <View style={wdStyles.row}>
                <View style={wdStyles.rowWeight}>
                  <Text style={wdStyles.rowWeightLabel}>{isRTL ? 'الوزن' : 'Weight'}</Text>
                  <Text style={wdStyles.rowWeightValue}>{Math.round(currentWeight)}</Text>
                  <Text style={wdStyles.rowPercent}>%</Text>
                </View>
                <View style={wdStyles.rowNameBox}>
                  <Text style={wdStyles.rowName} numberOfLines={1}>
                    {formData.name || (isRTL ? 'المؤشر الحالي' : 'Current KPI')}{isRTL ? ' (مؤشر أداء) ★' : ' (KPI) ★'}
                  </Text>
                </View>
              </View>

              {/* Existing items */}
              {weightItems.map((item, i) => {
                const key = `${item.type}-${item.id}-${i}`;
                return (
                  <View key={key} style={wdStyles.row}>
                    <View style={wdStyles.rowWeight}>
                      <Text style={wdStyles.rowWeightLabel}>{isRTL ? 'الوزن' : 'Weight'}</Text>
                      <TextInput
                        style={wdStyles.rowWeightInput}
                        value={modalWeights[key] ?? String(item.weight)}
                        onChangeText={(v) => setModalWeights(prev => ({ ...prev, [key]: normalizeArabicNumbers(v) }))}
                        keyboardType="numeric"
                        textAlign="center"
                      />
                      <Text style={wdStyles.rowPercent}>%</Text>
                    </View>
                    <View style={wdStyles.rowNameBox}>
                      <Text style={wdStyles.rowName} numberOfLines={1}>{item.name}</Text>
                    </View>
                  </View>
                );
              })}
            </ScrollView>

            {/* Remaining */}
            <View style={[wdStyles.remainingRow, modalRemaining < 0 && { borderColor: '#DC2626' }]}>
              <View style={wdStyles.rowWeight}>
                <Text style={[wdStyles.remainingValue, modalRemaining !== 0 && { color: '#DC2626' }]}>
                  {Math.round(modalRemaining)}
                </Text>
                <Text style={[wdStyles.rowPercent, modalRemaining !== 0 && { color: '#DC2626' }]}>%</Text>
              </View>
              <Text style={[wdStyles.remainingLabel, modalRemaining !== 0 && { color: '#DC2626' }]}>
                {isRTL ? 'الباقي' : 'Remaining'}
              </Text>
            </View>

            {/* Auto redistribute */}
            <TouchableOpacity onPress={autoRedistribute} style={wdStyles.autoBtn}>
              <Text style={wdStyles.autoBtnText}>{isRTL ? '⟲ أعادة توزيع تلقائي' : '⟲ Auto Redistribute'}</Text>
            </TouchableOpacity>

            {/* Footer buttons */}
            <View style={wdStyles.footer}>
              <TouchableOpacity
                onPress={handleSaveWeights}
                disabled={savingWeights || modalRemaining < 0}
                style={[wdStyles.saveBtn, (savingWeights || modalRemaining < 0) && { opacity: 0.5 }]}
              >
                {savingWeights
                  ? <ActivityIndicator size="small" color="#FFFFFF" />
                  : <Text style={wdStyles.saveBtnText}>{isRTL ? 'حفظ' : 'Save'}</Text>}
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setShowWeightModal(false)} style={wdStyles.cancelModalBtn}>
                <Text style={wdStyles.cancelModalBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </PageContainer>
  );
}

// ============================================================
// Weight Distribution Modal Styles
// ============================================================
const wdStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 520,
    maxHeight: '85%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#171717',
    textAlign: 'right',
    flex: 1,
  },
  specName: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#171717',
    textAlign: 'center',
    marginBottom: 8,
    fontWeight: '700',
  },
  description: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 8,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 10,
    marginBottom: 16,
    justifyContent: 'flex-end',
  },
  infoIcon: {
    fontSize: 14,
    color: '#94A3B8',
  },
  infoText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  rowWeight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  rowWeightLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#94A3B8',
  },
  rowWeightValue: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    minWidth: 30,
    textAlign: 'center',
  },
  rowWeightInput: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    minWidth: 48,
    textAlign: 'center',
  },
  rowPercent: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#94A3B8',
  },
  rowNameBox: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  rowName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  remainingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    marginBottom: 16,
    justifyContent: 'flex-end',
  },
  remainingLabel: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#22C55E',
  },
  remainingValue: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#22C55E',
  },
  autoBtn: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignSelf: 'flex-start',
    marginBottom: 20,
  },
  autoBtnText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
  },
  saveBtn: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minWidth: 80,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveBtnText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelModalBtn: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelModalBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  // Main Card
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    elevation: 2,
    overflow: 'visible',
  },
  // Page Title
  pageTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
  },
  // Section
  section: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    overflow: 'visible',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
  },
  // Field styles
  field: {
    marginBottom: 16,
  },
  fieldSmall: {
    width: '30%',
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
    overflow: 'visible',
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
    overflow: 'visible',
  },
  labelRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  requiredStar: {
    fontSize: 14,
    color: '#EF4444',
  },
  // Input styles
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    minHeight: 44,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  weightInput: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  percentSymbol: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  setWeightLabel: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  setWeightButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Toggle row (public KPI)
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 8,
    paddingVertical: 8,
  },
  toggleTextWrap: {
    flex: 1,
    alignItems: 'flex-end',
  },
  toggleLabel: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 4,
  },
  toggleDescription: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'right',
    lineHeight: 18,
  },
  customToggleTrack: {
    width: 48,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  customToggleThumb: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#FFFFFF',
  },
  customToggleThumbOn: {
    alignSelf: 'flex-end',
  },
  customToggleThumbOff: {
    alignSelf: 'flex-start',
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
    zIndex: 1,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownItemTextActive: {
    color: '#A9211F',
    fontWeight: '600',
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  saveButton: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minWidth: 80,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#D1D5DB',
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minHeight: 44,
    justifyContent: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Error styles
  errorText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 4,
  },
});
